import {ToasterConfig, ToasterModule, ToasterService} from 'angular2-toaster';
import {
    NavigationEnd,
    Router,
    RouterLink,
    RouterOutlet
} from '@angular/router';
import {Component, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {DOCUMENT, NgClass, NgFor, NgIf} from '@angular/common';
import {ModalModule} from 'ngx-bootstrap/modal';
import {InitializationAndConfigurationService} from './services/configuration/initializationAndConfiguration.service';
import {ApplicationService} from './services/application.service';
import {AdminService} from './services/admin.service';
import {Keepalive} from '@ng-idle/keepalive';
import {Subscription} from 'rxjs';
import {FormsModule} from '@angular/forms';
import {AppPasswordDirective} from './components/app-password.directive';
import {PasswordValidator, PasswordValidationRule} from './components/validators/password.validator';
import {PasswordAgainValidator} from './components/validators/password-again.validator';
import {ToDoListComponent} from './components/toDoList.component';
import {UserService} from './services/user.service';
import {HttpErrorsService} from './services/support/http-errors.service';
import {ToDoItemService} from './services/toDoItem.service';
import {FullScreenLoader} from './components/directives/fullScreenLoader.component';
import {BsDropdownModule} from 'ngx-bootstrap/dropdown';
import {AddSystemMessageComponent} from './components/system-messages/add-system-message.component';
import {ShowSystemMessagesComponent} from './components/system-messages/show-system-messages.component';

@Component({
    selector: 'body',
    styleUrls: ['app.component.scss'],
    templateUrl: './app.component.html',
    standalone: true,
    imports: [ToasterModule, ModalModule, RouterLink, RouterOutlet, AppPasswordDirective, PasswordValidator, PasswordAgainValidator,
        NgClass, NgIf, NgFor, FormsModule, ToDoListComponent, FullScreenLoader, BsDropdownModule, AddSystemMessageComponent, ShowSystemMessagesComponent]
})
export class AppComponent implements OnInit, OnDestroy {
    @ViewChild('changePasswordModal', {static: false}) changePasswordModal: any;
    @ViewChild('changePasswordForm', {static: false}) changePasswordForm: any;
    @ViewChild('toDoModal', {static: false}) toDoModal: any;
    @ViewChild('addSystemMessageModal', {static: false}) addSystemMessageModal: any;
    @ViewChild('showSystemMessagesModal', {static: false}) showSystemMessagesModal: any;

    public toasterConfig: ToasterConfig = new ToasterConfig({timeout: {error: 10000}});

    dashboardAvailablePermissionCount = 0;
    dashboardPermissionsArray = [
        {
            id: 'clinician',
            permission: 'DASHBOARD_VIEW_CLINICIAN',
            display: 'Clinician',
            link: 'dashboard/clinician/my',
            visible: false
        }
        , {
            id: 'supervisor',
            permission : 'DASHBOARD_VIEW_SUPERVISOR',
            display: 'Supervisor',
            link: 'dashboard/supervisor',
            visible: false
        }
        , {
            id: 'organizationalUnit',
            permission : 'DASHBOARD_VIEW_ORG_UNIT',
            display: 'Organizational Unit',
            link: 'dashboard/orgUnit',
            visible: false
        }
        , {
            id: 'highRisk',
            permission : 'DASHBOARD_VIEW_HIGH_RISK',
            display: 'High Risk',
            link: 'dashboard/highRisk',
            visible: false
        }
        , {
            id: 'clinicianAllocation',
            permission : 'DASHBOARD_VIEW_CLINICIAN_ALLOCATION',
            display: 'Clinician Allocation',
            link: 'dashboard/clinicianAllocation',
            visible: false
        }
        , {
            id: 'checklistsOrgUnit',
            permission : 'DASHBOARD_VIEW_ORG_UNIT',
            display: 'Checklists Org Unit',
            link: 'dashboard/checklists/orgunit',
            visible: false
        }
        , {
            id: 'modelOfCare',
            permission : 'DASHBOARD_VIEW_MODEL_OF_CARE',
            display: 'Program Fidelity',
            link: 'dashboard/modelOfCare',
            visible: false
        }
    ];
    singleDashboardLink: string; // TODO
    singleDashboardDisplay: string = 'DISPLAY'; // TODO
    user: any = {
        name: 'User'
    }

    public isOnDashboardRouter: boolean = true;
    public isOnSMR: boolean = false;


    public aggToDoItemCount = 0;
    public oldPassword: string;
    public newPassword: string;
    public repeatPassword: string;
    public changePasswordError: string;
    
    // Configurable password validation rules
    public passwordValidations: PasswordValidationRule[] = [
        { validation: '^.{7,}$', message: 'Password must be at least 7 characters long', regex: new RegExp('^.{7,}$') },
        { validation: '[0-9]', message: 'Password must have at least 1 number', regex: new RegExp('[0-9]') },
        { validation: '[a-zA-Z]', message: 'Password must have at least 1 letter', regex: new RegExp('[a-zA-Z]') },
        { validation: '[^a-zA-Z0-9]', message: 'Password must have at least 1 special character(!@#$%*, etc.)', regex: new RegExp('[^a-zA-Z0-9]') }
    ];
    
    // Example alternative validation rules you can use:
    // Simple 8+ character rule: [{ pattern: '^.{8,}$', message: 'Password must be at least 8 characters long' }]
    // Strong password with special chars: [
    //   { pattern: '^.{8,}$', message: 'Password must be at least 8 characters long' },
    //   { pattern: '[A-Z]', message: 'Password must contain at least one uppercase letter' },
    //   { pattern: '[a-z]', message: 'Password must contain at least one lowercase letter' },
    //   { pattern: '[0-9]', message: 'Password must contain at least one number' },
    //   { pattern: '[!@#$%^&*()_+\\-=\\[\\]{};:\\",./<>?]', message: 'Password must contain at least one special character' }
    // ]

    themeName: string;
    schooxCertificationImportEnabled: boolean;
    private applicationConfiguration: any;
    private navigationSubscription: Subscription;
    private loggedInUser: any;

    constructor(public initializationAndConfigurationService: InitializationAndConfigurationService,
                private applicationService: ApplicationService,
                private adminService: AdminService,
                private keepalive: Keepalive,
                private userService: UserService,
                private toDoItemService: ToDoItemService,
                private router: Router,
                private toasterService: ToasterService,
                @Inject(DOCUMENT) private doc: any) {
        this.navigationSubscription = this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                console.log('Navigation event:', event.url);
                if (event.url.includes('router')) {
                    this.isOnDashboardRouter = true;
                    this.isOnSMR = false;
                    console.log('Set to dashboard router mode');
                } else if (event.url.includes('data_entry')) {
                    this.isOnSMR = true;
                    this.isOnDashboardRouter = false;
                    console.log('Set to SMR mode');
                }
                console.log('isOnSMR:', this.isOnSMR, 'isOnDashboardRouter:', this.isOnDashboardRouter);
            }
        });
    }

    ngOnInit(): void {
        this.loggedInUser = this.initializationAndConfigurationService.getLoggedInUser();
        this.applicationConfiguration = this.initializationAndConfigurationService.getApplicationConfiguration();
        this.themeName = this.applicationConfiguration.themeName;
        this.schooxCertificationImportEnabled = this.applicationConfiguration.schooxCertificationImportEnabled;

        // Phase A: heartbeat only — keeps Tomcat HttpSession alive for users idle on a single page.
        // The full idle/warning/logout flow (with cross-tab coordination) lands when the Timeout branch is merged.
        this.keepalive.interval(this.applicationConfiguration.sessionKeepaliveInterval);
        this.keepalive.onPing.subscribe(() => this.adminService.keepAlive());
        this.keepalive.start();

        // Set the user name from the logged-in user
        if (this.loggedInUser) {
            console.log('Logged in user:', this.loggedInUser);
            this.user = {
                name: this.loggedInUser.name || this.loggedInUser.username || this.loggedInUser.displayName || 'User'
            };

            this.passwordValidations = this.initializationAndConfigurationService
                .getPasswordValidations(this.loggedInUser.highPrivilege);
        } else {
            console.warn('No logged in user found');
            // Set a default user name if no user is found
            this.user = {
                name: 'User'
            };
        }

        console.log('User object set to:', this.user);

        this.doc.getElementById('favicon')
            .setAttribute('href', 'images/favicons/' + this.initializationAndConfigurationService.getThemeName() + '_favicon.png');
        this.toDoItemService.getAggregateCount((count) => {
            this.aggToDoItemCount = count;
        });

        this.dashboardPermissionsArray.forEach((o: any) => {
            if ((this.applicationConfiguration.availableDashboardIdsArray.indexOf(o.id) >= 0) &&
                (this.userService.hasPermission(o.permission, this.loggedInUser))) {
                o.visible = true;
                this.dashboardAvailablePermissionCount++;
            }

            if (o.id === 'organizationalUnit') {
                o.display = this.applicationConfiguration.ouLabel.longText;
            }
        });

        // Crunch these two values just to save a few cycles during the digest refresh.
        if (this.dashboardAvailablePermissionCount === 1) {
            this.dashboardPermissionsArray.forEach((o: any) => {
                if (o.visible === true) {
                    this.singleDashboardDisplay = o.display;
                    this.singleDashboardLink = o.link;
                }
            });

        }

    }

    ngOnDestroy() {
        this.navigationSubscription.unsubscribe();
    }

    isItemActive(item: string): string {
        return null; // TODO
    }

    userHasPermission(permission: string): boolean {
        return this.userService.hasPermission(permission, this.loggedInUser);
    }

    openReport(): void {
        window.open(window.location.origin + '/smr/#REPORT_MANAGER', '_blank');
    }

    closeReport(): void {

    }

    openHelp(): void {
        // TODO
    }

    closeHelp(): void {

    }

    openToDoItemList(): void {
        this.toDoModal.config.backdrop = true;
        this.toDoModal.show()
    }

    closeToDoItemList(): void {
        this.toDoModal.hide();
    }

    openChangePassword(): void {
        // Clear the password fields and reset form state
        this.oldPassword = '';
        this.newPassword = '';
        this.repeatPassword = '';
        this.changePasswordError = ''; // Clear any previous error
        
        // Reset form validation state if form reference exists
        setTimeout(() => {
            if (this.changePasswordForm) {
                this.changePasswordForm.form.markAsPristine();
                this.changePasswordForm.form.markAsUntouched();
            }
        }, 0);
        
        this.changePasswordModal.config.backdrop = true;
        this.changePasswordModal.show();
    }

    closeChangePassword(): void {
        this.changePasswordModal.hide();
    }

    openAddSystemMessage(): void {
        this.addSystemMessageModal.config.backdrop = true;
        this.addSystemMessageModal.show();
    }

    closeAddSystemMessage(): void {
        this.addSystemMessageModal.hide();
    }

    openShowSystemMessages(): void {
        this.showSystemMessagesModal.config.backdrop = true;
        this.showSystemMessagesModal.show();
    }

    closeShowSystemMessages(): void {
        this.showSystemMessagesModal.hide();
    }

    onSystemMessageAdded(): void {
        // Refresh system messages if needed
        this.closeAddSystemMessage();
    }

    savePassword() {
        const changePasswordRequest = {
            newPassword: this.newPassword,
            oldPassword: this.oldPassword,
            repeatPassword: this.repeatPassword
        };

        this.userService.changePassword(changePasswordRequest).subscribe({
                next: (result: any) => {
                    this.toasterService.pop('success', 'Password successfully changed.');
                    this.closeChangePassword();
                },
                error: err => {
                    // Set the error message to display in the modal
                    if (err.error && err.error.message) {
                        this.changePasswordError = err.error.message;
                    } else if (err.error && err.error.error) {
                        this.changePasswordError = err.error.error;
                    } else if (err.message) {
                        this.changePasswordError = err.message;
                    } else {
                        this.changePasswordError = 'An error occurred while changing password';
                    }
                }
            }
        )
    };

    getToDoBadgeClass() {
        const severity = this.toDoItemService.getMaxSeverity();
        switch (severity) {
            case 0:
                return 'bg-success';
                break;
            case 1:
                return 'bg-warning';
                break;
            case 2:
                return 'bg-danger';
                break;
            default:
                return 'bg-success';
                break;
        }
    }
}